import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/AppIcon";
import BrandPanel from "../components/BrandPanel";
import { useAuth } from "../context/AuthContext";
import { ROOT_DOMAIN } from "../utils/host";
import {
  sanitizeSubdomainInput,
  slugifySubdomain,
  validateSubdomain,
} from "../utils/subdomain";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    institutionName: "",
    adminName: "",
    subdomain: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  // Track whether the admin has hand-edited the subdomain so we stop
  // auto-suggesting it from the institution name once they take control.
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      // Auto-suggest a subdomain from the institution name until the admin
      // edits the subdomain field themselves.
      if (name === "institutionName" && !subdomainTouched) {
        next.subdomain = slugifySubdomain(value);
      }
      return next;
    });
  };

  const onSubdomainChange = (e) => {
    setSubdomainTouched(true);
    const cleaned = sanitizeSubdomainInput(e.target.value);
    setForm((f) => ({ ...f, subdomain: cleaned }));
  };

  const validate = () => {
    const errs = {};
    if (form.institutionName.trim().length < 2)
      errs.institutionName = "Please enter your institution name.";
    if (form.adminName.trim().length < 2)
      errs.adminName = "Please enter your full name.";
    const subErr = validateSubdomain(form.subdomain);
    if (subErr) errs.subdomain = subErr;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Please enter a valid email address.";
    if (form.password.length < 8)
      errs.password = "Password must be at least 8 characters.";
    if (form.confirmPassword !== form.password)
      errs.confirmPassword = "Passwords do not match.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setLoading(true);
    try {
      await signup({
        institutionName: form.institutionName.trim(),
        adminName: form.adminName.trim(),
        subdomain: form.subdomain.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      navigate("/institution", { replace: true });
    } catch (err) {
      setError(err?.error || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BrandPanel />
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Icon name="GraduationCap" size={20} />
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold text-foreground">prepxai</div>
              <div className="-mt-0.5 text-[11px] text-muted-foreground">for institutions</div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground">
            Create your institution account
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up your placement cell console in a minute.
          </p>

          {error && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <Input
              label="Institution name"
              name="institutionName"
              placeholder="e.g. Springfield Institute of Technology"
              value={form.institutionName}
              onChange={onChange}
              error={fieldErrors.institutionName}
              required
            />
            <Input
              label="Your name"
              name="adminName"
              placeholder="e.g. Priya Sharma"
              value={form.adminName}
              onChange={onChange}
              error={fieldErrors.adminName}
              required
            />
            <div>
              <Input
                label="Console address"
                name="subdomain"
                placeholder="e.g. iitk"
                value={form.subdomain}
                onChange={onSubdomainChange}
                error={fieldErrors.subdomain}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="Globe" size={13} className="flex-shrink-0" />
                <span>
                  Your team will sign in at{" "}
                  <span className="font-medium text-foreground">
                    {form.subdomain || "your-college"}.{ROOT_DOMAIN}
                  </span>
                </span>
              </p>
            </div>
            <Input
              label="Work email"
              type="email"
              name="email"
              placeholder="you@college.edu"
              value={form.email}
              onChange={onChange}
              error={fieldErrors.email}
              required
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="At least 8 characters"
              value={form.password}
              onChange={onChange}
              error={fieldErrors.password}
              required
              autoComplete="new-password"
            />
            <Input
              label="Confirm password"
              type="password"
              name="confirmPassword"
              placeholder="Re-enter your password"
              value={form.confirmPassword}
              onChange={onChange}
              error={fieldErrors.confirmPassword}
              required
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              disabled={loading}
            >
              Create account
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/institution/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
